import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
    TextInput,
    PasswordInput,
    Button,
    Container,
    Paper,
    Title,
    Text,
    Stack,
    Alert,
    Center,
    Box
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import API_BASE_URL from "../config";

function Auth() {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const navigate = useNavigate();

    const handleRegister = async () => {
        try {
            await axios.post(`${API_BASE_URL}/api/auth/register`, { username, password });
            alert("Registration successful! Please log in.");
            setError("");
        } catch (err) {
            setError("Registration failed: " + (err.response?.data?.message || "Unknown error"));
        }
    };

    const handleLogin = async () => {
        try {
            await axios.post(`${API_BASE_URL}/api/auth/login`, null, {
                params: { username, password },
            });
            localStorage.setItem('username', username);
            navigate("/movies");
        } catch (err) {
            setError("Login failed: " + (err.response?.data?.message || "Unknown error"));
        }
    };

    return (
        <Box
            style={{
                height: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)'
            }}
        >
            <Container size={420}>
                <Paper
                    radius="xl"
                    p={50}
                    style={{
                        backgroundColor: 'rgba(28, 28, 30, 0.95)',
                        backdropFilter: 'blur(20px)',
                        color: 'white',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
                    }}
                >
                    {/* GSN Logo - Text Style */}
                    <Center mb="lg">
                        <div style={{
                            fontSize: '56px',
                            fontWeight: 700,
                            color: '#ffffff',
                            letterSpacing: '-2px',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
                        }}>
                            GSN
                        </div>
                    </Center>

                    <Title
                        align="center"
                        order={2}
                        mb="xs"
                        style={{
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '24px',
                            letterSpacing: '-0.3px'
                        }}
                    >
                        Sign in to GSN Cinema
                    </Title>

                    <Text
                        align="center"
                        size="sm"
                        color="dimmed"
                        mb={32}
                        style={{
                            fontSize: '12px',
                            letterSpacing: '0.3px'
                        }}
                    >
                        Premium Entertainment Experience
                    </Text>

                    {error && (
                        <Alert
                            icon={<IconAlertCircle size={16} />}
                            title="Error"
                            color="red"
                            variant="filled"
                            mb="md"
                            radius="md"
                            styles={{
                                root: {
                                    backgroundColor: 'rgba(250, 82, 82, 0.1)',
                                    border: '1px solid rgba(250, 82, 82, 0.3)'
                                }
                            }}
                        >
                            {error}
                        </Alert>
                    )}

                    <Stack spacing="lg">
                        <TextInput
                            placeholder="Username"
                            value={username}
                            onChange={(e) => setUsername(e.currentTarget.value)}
                            size="md"
                            radius="md"
                            styles={{
                                input: {
                                    backgroundColor: '#2c2c2e',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    color: 'white',
                                    fontSize: '15px',
                                    padding: '12px 16px',
                                    '&::placeholder': { color: '#86868b' },
                                    '&:focus': {
                                        borderColor: '#0a84ff',
                                        boxShadow: '0 0 0 3px rgba(10, 132, 255, 0.1)'
                                    }
                                }
                            }}
                        />
                        <PasswordInput
                            placeholder="Password"
                            value={password}
                            onChange={(e) => setPassword(e.currentTarget.value)}
                            size="md"
                            radius="md"
                            styles={{
                                input: {
                                    backgroundColor: '#2c2c2e',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    color: 'white',
                                    fontSize: '15px',
                                    padding: '12px 16px',
                                    '&::placeholder': { color: '#86868b' },
                                    '&:focus': {
                                        borderColor: '#0a84ff',
                                        boxShadow: '0 0 0 3px rgba(10, 132, 255, 0.1)'
                                    }
                                }
                            }}
                        />

                        <Button
                            fullWidth
                            size="md"
                            radius="xl"
                            onClick={handleLogin}
                            style={{
                                backgroundColor: '#0a84ff',
                                height: '48px',
                                fontSize: '16px',
                                fontWeight: 600,
                                transition: 'all 0.2s',
                                border: 'none'
                            }}
                            styles={{
                                root: {
                                    '&:hover': {
                                        backgroundColor: '#0077ed',
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 4px 12px rgba(10, 132, 255, 0.4)'
                                    }
                                }
                            }}
                        >
                            Sign In
                        </Button>

                        <Button
                            fullWidth
                            variant="subtle"
                            size="sm"
                            color="gray"
                            onClick={handleRegister}
                            style={{
                                color: '#86868b',
                                fontSize: '14px'
                            }}
                            styles={{
                                root: {
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                        color: '#fff'
                                    }
                                }
                            }}
                        >
                            Create Account
                        </Button>
                    </Stack>

                    <Text
                        align="center"
                        size="xs"
                        color="dimmed"
                        mt="xl"
                        style={{
                            fontSize: '11px',
                            opacity: 0.5
                        }}
                    >
                        © 2025 GSN Cinema. All rights reserved.
                    </Text>
                </Paper>
            </Container>
        </Box>
    );
}

export default Auth;
